// material-ui
import {
  Button,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import BlockIcon from '@material-ui/icons/Block';
import CheckIcon from '@material-ui/icons/Check';
import DeleteIcon from '@material-ui/icons/Delete';
import InfoIcon from '@material-ui/icons/Info';
import { cloneDeep, set } from 'lodash-es';
import React, { ReactElement } from 'react';

// apis
import { CampaignActiveActive, IAdserverCampaign } from '~/src/dash/api/campaign/campaign';
import { createCompany, deleteCompany, ECompanyType, updateCompany } from '~/src/dash/api/company';
import {
  EAllowStatus,
  IAdserverAlias,
  makeTargetingChangeDescription,
  makeTargetingChangeInfoHover,
  ResourceActiveDisabled,
  ResourceActiveEnabled,
} from '~/src/dash/api/console-api';
import { FrequencyCapUnitDays, FrequencyCapUnitHours } from '~/src/dash/api/frequency-cap';
import {
  AdserverOrderFrequencyCapActiveActive,
  AdserverOrderFrequencyCapActiveInactive,
  AdserverOrderFrequencyCapTargetingKeyDuration,
  AdserverOrderFrequencyCapTargetingKeyImpression,
  IAdserverOrder,
  IAdserverOrderFrequencyCap,
} from '~/src/dash/api/order';
import { EBool } from '~/src/dash/api/types/bool';
// components
import { AddEditCompanyModel, companyTypes } from '~/src/dash/components/add-edit-company-model';
import ConfirmationDialogButton from '~/src/dash/components/confirmation-dialog-button';
import { DaypartingSelector, IDaypartingInterval } from '~/src/dash/components/dayparting-selector';
import { CompanyMultiSelect } from '~/src/dash/components/dropdowns/company-multi-select';
import { UserMultiSelect } from '~/src/dash/components/dropdowns/user-multi-select';
import { FocusedTextfield } from '~/src/dash/components/focused-textfield';
import { LiveData } from '~/src/dash/components/live-data/live-data';
import { MaterialLabelDropdownMulti } from '~/src/dash/components/material-label-dropdown-multi';
import { ModelTopBar } from '~/src/dash/components/model-topbar';
import { SettingsAppBar } from '~/src/dash/components/settings-app-bar';
import { ITab, SettingsTabs } from '~/src/dash/components/settings-tabs';
import { Panel, Panels, spacing } from '~/src/dash/publicaUI';
// other
import { IStore } from '~/src/dash/reducers/store';
import { extractRouteParams, navigate, RouteCampaignNew } from '~/src/dash/router/routes';
import {
  IBaseState,
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { publicaColors } from '~/src/dash/utils/colors';
// utils
import { decorate } from '~/src/dash/utils/decorator';
import { obfuscateOrderName } from '~/src/dash/utils/obfuscate';
import {
  getOrderPacingTypeOption,
  getOrderPacingTypeOptionByText,
  orderPacingTypeOptions,
} from '~/src/dash/utils/pacing-type-options';
import { IS_AUTHORIZED } from '~/src/dash/utils/permission_service';
import { routeName } from '~/src/dash/utils/routes';
// views
import { CampaignSettingsListView } from '~/src/dash/views/campaign/campaign-settings-list-view';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {
  daypartingIntervals: IDaypartingInterval[];
  handleChangeDayparting: (
    event: React.ChangeEvent<HTMLInputElement>,
    adserverOrder?: IAdserverOrder,
  ) => void;
  handleChangeAlias: (name: IAdserverAlias[]) => void;
  handleCreateDaypartingPeriod: () => void;
  handleDaypartingDateChange: (idx: number, time: string, dateType: string) => void;
  handleDaypartingDayClick: (day: string, idx: number) => void;
  handleDeleteDaypartingPeriod: (idx: number) => void;
  isDaypartingActive: boolean;
  order: IAdserverOrder;
  onChange: (order: IAdserverOrder) => void; // updates parent state and rerenders
  onSubmit: (order: IAdserverOrder) => void; // saves/creates order
  onClose: () => void; // navigates to parent order or line item list
  openActiveDialog: () => void; // opens active modal if the order is existing and simply switches active if new/cloned
  openPacingDialog: () => void; // opens pacing dialog
  getOrder: () => void; // tells the view to get the order
  uniqueOrderAliases: IAdserverAlias[];
}

interface IState extends IBaseState {
  // error msging to user
  snackbarMessage: string;
  snackbarError: boolean;
  // indicates an api call to update is in progress
  isUpdating: boolean;
  // delete modal bools
  deletePopupActive: boolean;
  AddEditCompanyModelActive: boolean;
  lastPressedAddEditCompanyModelType: ECompanyType;
  // increments are updates to company occurs to tell children to get companies from api
  companyUpdateCounter: number;
}

function isExistingOrder(order: IAdserverOrder): boolean {
  return !!order.ID;
}

export const OrderSettingsForm = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    // NOTE: Use styles when the CSS is used more than once
    styles: () => ({
      centeredSwitch: {
        display: 'inline-flex',
        alignItems: 'center',
        marginLeft: '10px',
        marginRight: '40px',
      },
      formRow: {
        margin: 8,
      },
      inputLabel: {
        fontSize: '16px',
        fontWeight: 500,
        color: 'black',
        width: '170px',
      },
      itemTypography: {
        fontSize: 16,
        fontWeight: 400,
      },
      spanActive: {
        display: 'inline-flex',
        alignItems: 'center',
        width: 80,
        fontWeight: 'bold',
      },
      spanDescription: {
        display: 'inline-flex',
        alignItems: 'center',
        margin: '10px 10px',
        fontSize: 15,
      },
      titleTypography: {
        fontSize: 16,
        fontWeight: 500,
      },
    }),
    router: true,
  },
  class extends React.Component<IProps, IState> {
    state = {
      companyUpdateCounter: 0,
      deletePopupActive: false,
      isUpdating: false,
      lastPressedAddEditCompanyModelType: ECompanyType.TYPE_UNKNOWN,
      snackbarError: false,
      snackbarMessage: '',
      AddEditCompanyModelActive: false,
    };

    // -------- HANDLER FUNCTIONS -------- //
    // NOTE: functions passed into a child component which update state in the parent
    // should be called handleFunc.  They are used to not directly modify the props/state
    // and make sure the parent component is properly re-rendered

    // -------- SETTINGS TOP BAR HANDLER FUNCTIONS -------- //

    handleNameChange = (name: string): void => {
      this.props.onChange({ ...this.props.order, Name: name });
    };

    // if the order is existing, open the modal to check if the user wants to switch all line items to active/inactive
    // otherwise, simply switch active
    handleChangeActive = (): void => {
      if (isExistingOrder(this.props.order)) {
        this.props.openActiveDialog();
      } else {
        this.props.onChange({
          ...this.props.order,
          Active:
            this.props.order.Active === ResourceActiveEnabled
              ? ResourceActiveDisabled
              : ResourceActiveEnabled,
        });
      }
    };

    // -------- SNACKBAR HELPER FUNCTIONS -------- //
    handleSnackbarError = (snackbarError: boolean, snackbarMessage: string): void => {
      this.setState({
        snackbarError,
        snackbarMessage,
      });
    };

    handleSnackbarClose = (): void => {
      this.setState({
        snackbarMessage: '',
        snackbarError: false,
      });
    };

    // -------- FORM UPDATE FUNCTIONS -------- //

    // a generic form handler for form input elements.
    handleFormChange = (elem: React.ChangeEvent<HTMLInputElement>): void => {
      const { type, name: path, value, checked } = elem.target;
      let parsedValue;
      switch (type) {
        // for input[type=number] fields
        case 'number':
          parsedValue = parseInt(value, 10);
          break;
        // <Checkbox/> returned a boolean on the checked property of the target
        case 'checkbox':
          parsedValue = checked;
          break;
        // <RadioGroup/> return the value of the <Radio> element
        case 'radio':
          parsedValue = path === 'AudienceTargetingOperationType' ? parseInt(value, 10) : value;
          break;
        // Everything else including <input> fields return strings
        default:
          parsedValue = value;
      }

      // Create a new order so that we can bubble up change
      const newOrder = { ...this.props.order };

      // path can be a top level deal property like "Name" for Order.Name or go deeper such as
      // "SspParams.PackageID" for Deal.SspParams.PackageID
      set<IAdserverOrder>(newOrder, path, parsedValue);

      // bubble up event
      this.props.onChange(newOrder);
    };

    // First ID is taken since the dropdowns, while normally multi-select, are set to single
    handleChangeAdvertiser = (advertiserIDs: number[]) => {
      if (advertiserIDs.length) {
        this.props.onChange({
          ...this.props.order,
          AdvertiserID: advertiserIDs[0],
        });
      } else {
        this.props.onChange({
          ...this.props.order,
          AdvertiserID: 0,
        });
      }
    };

    // First ID is taken since the dropdowns, while normally multi-select, are set to single
    handleChangeAgency = (agencyIDs: number[]) => {
      if (agencyIDs.length) {
        this.props.onChange({
          ...this.props.order,
          AgencyID: agencyIDs[0],
        });
      } else {
        this.props.onChange({
          ...this.props.order,
          AgencyID: 0,
        });
      }
    };

    // First ID is taken since the dropdowns, while normally multi-select, are set to single
    handleChangeTrafficker = (traffickerIDs: string[]) => {
      if (traffickerIDs.length) {
        this.props.onChange({
          ...this.props.order,
          TraffickerID: parseInt(traffickerIDs[0], 10) || 0,
        });
      } else {
        this.props.onChange({
          ...this.props.order,
          TraffickerID: 0,
        });
      }
    };

    // First ID is taken since the dropdowns, while normally multi-select, are set to single
    handleChangeSalesperson = (salespersonIDs: string[]) => {
      if (salespersonIDs.length) {
        this.props.onChange({
          ...this.props.order,
          SalespersonID: parseInt(salespersonIDs[0], 10) || 0,
        });
      } else {
        this.props.onChange({
          ...this.props.order,
          SalespersonID: 0,
        });
      }
    };

    handleAddNewFrequencyCap = (): void => {
      const { onChange, order } = this.props;
      const clonedFrequencyCaps = cloneDeep(order.AdserverOrderFrequencyCaps);
      clonedFrequencyCaps.push({
        ID: 0,
        CreatedAt: new Date(),
        UpdatedAt: new Date(),
        DeletedAt: null,
        Active: AdserverOrderFrequencyCapActiveActive,
        AdserverOrderID: order.ID,
        Limit: 0,
        TimeToLive: 2592000,
        TargetingKey: AdserverOrderFrequencyCapTargetingKeyImpression,
      });
      onChange(
        Object.assign({}, order, {
          AdserverOrderFrequencyCaps: clonedFrequencyCaps,
        }),
      );
    };

    handleChangeFrequencyCap = (
      { target }: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | { value: any }>,
      field: string,
      frequencyCapIdx: number,
    ) => {
      const { onChange, order } = this.props;
      const clonedFrequencyCaps = cloneDeep(order.AdserverOrderFrequencyCaps);
      const foundFrequencyCap = clonedFrequencyCaps[frequencyCapIdx];
      const frequencyLimitUnit = this.calcFrequencyCapUnit(foundFrequencyCap);

      if (foundFrequencyCap) {
        switch (field) {
          case 'Switch':
            if (foundFrequencyCap.Active === AdserverOrderFrequencyCapActiveActive) {
              foundFrequencyCap.Active = AdserverOrderFrequencyCapActiveInactive;
            } else {
              foundFrequencyCap.Active = AdserverOrderFrequencyCapActiveActive;
            }
            break;
          case 'Limit':
            foundFrequencyCap.Limit = parseInt(target.value, 10) || 0;
            break;
          case 'TargetingKey':
            foundFrequencyCap.TargetingKey = parseInt(target.value, 10) || 0;
            break;
          case 'TimeToLive':
            if (frequencyLimitUnit === FrequencyCapUnitDays) {
              foundFrequencyCap.TimeToLive = (parseInt(target.value, 10) || 0) * 86400;
            } else if (frequencyLimitUnit === FrequencyCapUnitHours) {
              foundFrequencyCap.TimeToLive = (parseInt(target.value, 10) || 0) * 3600;
            }
            break;
          case 'Unit':
            if (
              target.value === FrequencyCapUnitDays &&
              frequencyLimitUnit === FrequencyCapUnitHours
            ) {
              foundFrequencyCap.TimeToLive = foundFrequencyCap.TimeToLive * 24;
            } else if (
              target.value === FrequencyCapUnitHours &&
              frequencyLimitUnit === FrequencyCapUnitDays
            ) {
              foundFrequencyCap.TimeToLive = foundFrequencyCap.TimeToLive / 24;
            }
            break;
        }
      }

      onChange(
        Object.assign({}, this.props.order, {
          AdserverOrderFrequencyCaps: clonedFrequencyCaps,
        }),
      );
    };

    handleDeleteFrequencyCap = (frequencyCapIdx: number): void => {
      const { onChange, order } = this.props;
      const clonedFrequencyCaps = cloneDeep(order.AdserverOrderFrequencyCaps);
      clonedFrequencyCaps.splice(frequencyCapIdx, 1);
      onChange(
        Object.assign({}, this.props.order, {
          AdserverOrderFrequencyCaps: clonedFrequencyCaps,
        }),
      );
    };

    addNewLineItem = (): void => {
      navigate(RouteCampaignNew, extractRouteParams(this.props), {
        order: this.props.order.ID.toString(),
      });
    };

    setAddEditCompanyModelActive = (active: boolean) => {
      this.setState({
        AddEditCompanyModelActive: active,
      });
    };

    setLastPressedAddEditCompanyModelOptionName = (companyType: ECompanyType) => {
      this.setState({
        lastPressedAddEditCompanyModelType: companyType,
      });
    };

    onOpenAddEditCompanyModelHandler = (lastPressedAddEditCompanyModelOptionName: ECompanyType) => {
      /**
       * Used to set the companyModel to active, and to keep track
       * of the name of the last CompanyMultiSelect dropdown we clicked.
       * This name is used to set the default dropdown option in AddEditCompanyModel.
       */
      this.setAddEditCompanyModelActive(true);
      this.setLastPressedAddEditCompanyModelOptionName(lastPressedAddEditCompanyModelOptionName);
    };

    addOrUpdateCompany = (Name: string, CompanyType: ECompanyType, ID: number) => {
      this.setState(
        {
          isUpdating: true,
        },
        () => {
          if (ID === 0) {
            createCompany(
              {
                ID: 0,
                CompanyType,
                Name,
                PublisherID: parseInt(this.props.session.publisher.ID, 10),
              },
              this.props.session.publisher.ID,
            )
              .then(() => {
                this.setState({
                  AddEditCompanyModelActive: false,
                  isUpdating: false,
                  snackbarMessage: 'Company created successfully.',
                  companyUpdateCounter: this.state.companyUpdateCounter + 1,
                });
              })
              .catch(() => {
                this.setState({
                  AddEditCompanyModelActive: false,
                  isUpdating: false,
                  snackbarMessage: 'Failed to create company',
                  snackbarError: true,
                });
              });
          } else {
            updateCompany(
              {
                ID,
                CompanyType,
                Name,
                PublisherID: parseInt(this.props.session.publisher.ID, 10),
              },
              this.props.session.publisher.ID,
              false,
            )
              .then(() => {
                this.setState({
                  AddEditCompanyModelActive: false,
                  isUpdating: false,
                  snackbarMessage: 'Company updated successfully.',
                  companyUpdateCounter: this.state.companyUpdateCounter + 1,
                });
              })
              .catch(() => {
                this.setState({
                  AddEditCompanyModelActive: false,
                  isUpdating: false,
                  snackbarMessage: 'Failed to update company',
                  snackbarError: true,
                });
              });
          }
        },
      );
    };

    deleteCompany = (id: number) => {
      this.setState(
        {
          isUpdating: true,
        },
        () => {
          deleteCompany(id.toString(), this.props.session.publisher.ID)
            .then(() => {
              this.setState({
                AddEditCompanyModelActive: false,
                isUpdating: false,
                snackbarMessage: `Company ${id} deleted successfully`,
                companyUpdateCounter: this.state.companyUpdateCounter + 1,
              });
            })
            .catch(() => {
              this.setState({
                isUpdating: false,
                snackbarMessage: `Company ${id} deletion failed`,
                snackbarError: true,
              });
            });
        },
      );
    };

    // -------- MISC HELPER FUNCTIONS ---------- //
    calcFrequencyCapUnit = (frequencyCap: IAdserverOrderFrequencyCap): string => {
      // if the time to live (in seconds) is not divisible by the # of seconds in 1 day, set unit to hours
      if (frequencyCap.TimeToLive % 86400 !== 0) {
        return FrequencyCapUnitHours;
      }
      return FrequencyCapUnitDays;
    };

    calcFrequencyCapDuration = (frequencyCap: IAdserverOrderFrequencyCap): number => {
      const frequencyCapUnit = this.calcFrequencyCapUnit(frequencyCap);
      const frequencyCapTimeToLive = frequencyCap.TimeToLive;
      if (frequencyCapUnit === FrequencyCapUnitDays) {
        return frequencyCapTimeToLive / 86400;
      } else {
        return frequencyCapTimeToLive / 3600;
      }
    };

    // -------- RENDER HELPER FUNCTIONS -------- //
    // NOTE: We destructure props and states used in render functions to clearly
    // show what we are passing as well as keeping the code clean and easier to read

    // renders top bar => name edit, save, close, delete
    renderTopBar(): ReactElement {
      const { isUpdating } = this.state;
      const { order, onSubmit, onClose, openPacingDialog } = this.props;

      const pacingTypeNone = order.PacingType === 4;
      const pacingTypeMismatch = order.LineItems.some(
        (lineItem) => lineItem.PacingType.toString() !== order.PacingType.toString(),
      );
      return (
        <Grid item>
          <SettingsAppBar
            name={obfuscateOrderName(order, this.props.user.user.Username, order.Name)}
            isActive={order.Active === ResourceActiveEnabled}
            hasWriteRights={true}
            isUpdating={isUpdating}
            handleNameChange={this.handleNameChange}
            handleSave={() =>
              pacingTypeMismatch && !pacingTypeNone ? openPacingDialog() : onSubmit(order)
            }
            handleClose={onClose}
            handleChangeActive={this.handleChangeActive}
            resourceID={order.ID}
            relative
          />
        </Grid>
      );
    }

    renderDetails = () => {
      const {
        classes,
        daypartingIntervals,
        handleChangeDayparting,
        handleCreateDaypartingPeriod,
        handleDaypartingDateChange,
        handleDaypartingDayClick,
        handleDeleteDaypartingPeriod,
        isDaypartingActive,
        onChange,
        order,
        user,
      } = this.props;
      const { companyUpdateCounter } = this.state;
      const advertiserIDs = [] as number[];
      // these need to be arrays since multi-select dropdown can have multiple selections
      // but these are set to single select
      if (order.AdvertiserID) {
        advertiserIDs.push(order.AdvertiserID);
      }
      const agencyIDs = [] as number[];
      if (order.AgencyID) {
        agencyIDs.push(order.AgencyID);
      }
      const salespersonIDs = [] as string[];
      if (order.SalespersonID) {
        salespersonIDs.push(order.SalespersonID.toString());
      }
      const traffickerIDs = [] as string[];
      if (order.TraffickerID) {
        traffickerIDs.push(order.TraffickerID.toString());
      }

      const writeAuthorization = IS_AUTHORIZED(['write_campaign'], user.user);
      return (
        <Grid
          item
          style={{
            width: '100%',
            marginTop: 15,
            marginLeft: 20,
            height: '100%',
            paddingBottom: 50,
          }}
        >
          <Grid container spacing={2} direction='column'>
            {/* Required */}
            <Grid
              item
              style={{
                width: '100%',
                maxWidth: 800,
                marginTop: 15,
                marginLeft: 25,
              }}
            >
              {/* Advertiser */}
              <Grid item>
                <Typography className={classes.titleTypography}>Required</Typography>
              </Grid>
              <Grid container alignItems='center' spacing={2} className={classes.formRow}>
                <Grid item xs={2}>
                  <Typography className={classes.itemTypography}>Advertiser</Typography>
                </Grid>
                <Grid item xs={8}>
                  <CompanyMultiSelect
                    onChange={this.handleChangeAdvertiser}
                    publisherID={this.props.session.publisher.ID}
                    companyIDs={advertiserIDs}
                    enableMultiSelect={false}
                    companyTypesAllowed={[
                      ECompanyType.TYPE_ADVERTISER,
                      ECompanyType.TYPE_AD_NETWORK,
                      ECompanyType.TYPE_HOUSE_ADVERTISER,
                    ]}
                    openAddEditCompanyModelHandler={() =>
                      this.onOpenAddEditCompanyModelHandler(ECompanyType.TYPE_ADVERTISER)
                    }
                    counter={companyUpdateCounter}
                    menuTitle={companyTypes[ECompanyType.TYPE_ADVERTISER].text}
                    username={this.props.user.user.Username}
                  />
                </Grid>
              </Grid>
              {/* Trafficker */}
              <Grid container alignItems='center' spacing={2} className={classes.formRow}>
                <Grid item xs={2}>
                  <Typography className={classes.itemTypography}>Trafficker</Typography>
                </Grid>
                <Grid item xs={8}>
                  <UserMultiSelect
                    onChange={this.handleChangeTrafficker}
                    publisherID={this.props.session.publisher.ID}
                    enableMultiSelect={false}
                    userIDs={traffickerIDs}
                    username={this.props.user.user.Username}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Divider style={{ margin: 10, height: 1, color: 'black' }} />
            {/* Optional */}
            <Grid
              item
              style={{
                width: '100%',
                maxWidth: 800,
                marginTop: 5,
                marginLeft: 25,
              }}
            >
              <Grid item>
                <Typography className={classes.titleTypography}>Optional</Typography>
              </Grid>
              {/* Agency */}
              <Grid container alignItems='center' spacing={2} className={classes.formRow}>
                <Grid item xs={2}>
                  <Typography className={classes.itemTypography}>Agency</Typography>
                </Grid>
                <Grid item xs={8}>
                  <CompanyMultiSelect
                    onChange={this.handleChangeAgency}
                    publisherID={this.props.session.publisher.ID}
                    companyIDs={agencyIDs}
                    enableMultiSelect={false}
                    companyTypesAllowed={[ECompanyType.TYPE_AGENCY]}
                    openAddEditCompanyModelHandler={() =>
                      this.onOpenAddEditCompanyModelHandler(ECompanyType.TYPE_AGENCY)
                    }
                    counter={companyUpdateCounter}
                    menuTitle={companyTypes[ECompanyType.TYPE_AGENCY].text}
                    username={this.props.user.user.Username}
                  />
                </Grid>
              </Grid>
              {/* Salesperson */}
              <Grid container alignItems='center' spacing={2} className={classes.formRow}>
                <Grid item xs={2}>
                  <Typography className={classes.itemTypography}>Salesperson</Typography>
                </Grid>
                <Grid item xs={8}>
                  <UserMultiSelect
                    onChange={this.handleChangeSalesperson}
                    publisherID={this.props.session.publisher.ID}
                    enableMultiSelect={false}
                    userIDs={salespersonIDs}
                    maxHeight={300}
                    username={this.props.user.user.Username}
                  />
                </Grid>
              </Grid>
              {/* PO number */}
              <Grid container alignItems='center' spacing={2} className={classes.formRow}>
                <Grid item xs={2}>
                  <Typography className={classes.itemTypography}>PO Number</Typography>
                </Grid>
                <Grid item xs={8}>
                  <FocusedTextfield
                    key={'PurchaseOrder'}
                    fullWidth
                    multiline
                    label=''
                    name={'PurchaseOrder'}
                    value={order.PurchaseOrder}
                    onChange={this.handleFormChange}
                  />
                </Grid>
              </Grid>
              {/* Notes => used to be label, but we want to change this to many-to-many like channel labels */}
              <Grid container alignItems='center' spacing={2} className={classes.formRow}>
                <Grid item xs={2}>
                  <Typography className={classes.itemTypography}>Notes</Typography>
                </Grid>
                <Grid item xs={8}>
                  <FocusedTextfield
                    key='Labels'
                    fullWidth
                    multiline
                    label=''
                    name='Labels'
                    value={order.Labels}
                    onChange={this.handleFormChange}
                  />
                </Grid>
              </Grid>
              {/* Pacing */}
              <Grid container alignItems='center' spacing={2} className={classes.formRow}>
                <Grid item xs={2}>
                  <Typography className={classes.itemTypography}>Pacing</Typography>
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    margin='normal'
                    name='PacingType'
                    onChange={({ target }) => {
                      const pacingTypeOption = getOrderPacingTypeOptionByText(target.value);
                      if (pacingTypeOption) {
                        onChange(
                          Object.assign({}, order, {
                            PacingType: pacingTypeOption.value,
                          }),
                        );
                      }
                    }}
                    select={true}
                    style={{ width: 200 }}
                    value={(() => {
                      const pacingTypeOption = getOrderPacingTypeOption(order.PacingType);
                      if (pacingTypeOption) {
                        return pacingTypeOption.text;
                      }
                      return '';
                    })()}
                  >
                    {orderPacingTypeOptions.map(({ text, value }) => {
                      return (
                        <MenuItem key={value} value={text}>
                          {text}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                </Grid>
                <Grid item xs={2} style={{ marginLeft: 10 }}>
                  <Tooltip
                    title='When Order Pacing is applied, it will apply to every individual Line Item under this Order.'
                    placement='bottom-start'
                  >
                    <InfoIcon
                      style={{
                        verticalAlign: 'bottom',
                        width: '1rem',
                        color: '#929599',
                        marginTop: 10,
                      }}
                    />
                  </Tooltip>
                </Grid>
              </Grid>
              {/* Alias */}
              <Grid container alignItems='center' spacing={2} className={classes.formRow}>
                <Grid item xs={2}>
                  <Grid container direction='row'>
                    <Typography className={classes.itemTypography}>Alias</Typography>
                    <Tooltip title='Creating an alias allows you to filter and group by that alias in reporting. NOTE: an alias cannot include commas'>
                      <InfoIcon style={{ marginLeft: 5, width: '0.9rem', color: '#929599' }} />
                    </Tooltip>
                  </Grid>
                </Grid>
                <Grid xs={8}>
                  {
                    <div className={classes.optionRow}>
                      <MaterialLabelDropdownMulti
                        menuTitle='allows for one alias input'
                        list={this.props.uniqueOrderAliases}
                        selected={
                          this.props.order.Alias && this.props.order.Alias.length > 0
                            ? [{ Name: this.props.order.Alias } as IAdserverAlias]
                            : []
                        }
                        disableMultiSelect={EBool.EBool_TRUE}
                        onChange={(list: IAdserverAlias[]) => {
                          this.props.handleChangeAlias(list);
                        }}
                      />
                    </div>
                  }
                </Grid>
              </Grid>
            </Grid>

            <Divider style={{ margin: 10, height: 1, color: 'black' }} />

            {/* Frequency Capping */}
            <Grid
              container
              style={{
                width: '100%',
                maxWidth: 960,
                marginTop: 5,
                marginLeft: 25,
              }}
            >
              <Typography className={classes.titleTypography}>Frequency Capping</Typography>
              <Grid item style={{ marginLeft: 10 }}>
                <Tooltip
                  title='When Order Frequency Capping is applied, it will apply across all Line Items under this Order.'
                  placement='bottom-start'
                >
                  <InfoIcon
                    style={{
                      verticalAlign: 'bottom',
                      width: '1rem',
                      color: '#929599',
                      marginTop: 1,
                    }}
                  />
                </Tooltip>
              </Grid>
              <Grid container>
                {order.AdserverOrderFrequencyCaps.map(
                  (frequencyCap: IAdserverOrderFrequencyCap, frequencyCapIdx: number) => {
                    return (
                      <Grid container direction='row'>
                        <span
                          className={classes.spanActive}
                          style={{ padding: '20px 0 15px 20px', margin: '5px 0' }}
                        >
                          {frequencyCap.Active === AdserverOrderFrequencyCapActiveActive
                            ? 'Active'
                            : 'Inactive'}
                        </span>

                        <Grid item style={{ marginTop: 15 }}>
                          <Switch
                            disabled={!writeAuthorization.isAuthorized}
                            className={classes.centeredSwitch}
                            checked={frequencyCap.Active === AdserverOrderFrequencyCapActiveActive}
                            onChange={(event) =>
                              this.handleChangeFrequencyCap(event, 'Switch', frequencyCapIdx)
                            }
                            value={frequencyCap.Active}
                            color='secondary'
                          />
                        </Grid>

                        {frequencyCap.Active === AdserverOrderFrequencyCapActiveActive && (
                          <>
                            <Grid item style={{ maxWidth: 150, marginTop: 15 }}>
                              <Select
                                name='TargetingKey'
                                style={{ width: 150 }}
                                value={frequencyCap.TargetingKey}
                                disabled={
                                  frequencyCap.Active === AdserverOrderFrequencyCapActiveInactive
                                }
                                onChange={(event: React.ChangeEvent<{ value: any }>) =>
                                  this.handleChangeFrequencyCap(
                                    event,
                                    'TargetingKey',
                                    frequencyCapIdx,
                                  )
                                }
                              >
                                <MenuItem
                                  key={AdserverOrderFrequencyCapTargetingKeyImpression}
                                  value={AdserverOrderFrequencyCapTargetingKeyImpression}
                                  style={{ width: '100%' }}
                                >
                                  Impression
                                </MenuItem>
                                <MenuItem
                                  key={AdserverOrderFrequencyCapTargetingKeyDuration}
                                  value={AdserverOrderFrequencyCapTargetingKeyDuration}
                                  style={{ width: '100%' }}
                                >
                                  Duration
                                </MenuItem>
                              </Select>
                            </Grid>

                            <span className={classes.spanDescription} style={{ marginTop: 20 }}>
                              Set limit to
                            </span>

                            <Grid item style={{ maxWidth: 75, marginTop: 5 }}>
                              <FocusedTextfield
                                label='Frequency'
                                style={{ width: '100%' }}
                                value={frequencyCap.Limit}
                                disabled={
                                  frequencyCap.Active === AdserverOrderFrequencyCapActiveInactive
                                }
                                onChange={(event) =>
                                  this.handleChangeFrequencyCap(event, 'Limit', frequencyCapIdx)
                                }
                              />
                            </Grid>

                            <span className={classes.spanDescription} style={{ marginTop: 20 }}>
                              {frequencyCap.TargetingKey ===
                              AdserverOrderFrequencyCapTargetingKeyImpression
                                ? 'impressions'
                                : 'seconds'}{' '}
                              per user per
                            </span>

                            <Grid item style={{ maxWidth: 75, marginTop: 5 }}>
                              <FocusedTextfield
                                label='Duration'
                                style={{ width: '100%' }}
                                value={this.calcFrequencyCapDuration(frequencyCap)}
                                disabled={
                                  frequencyCap.Active === AdserverOrderFrequencyCapActiveInactive
                                }
                                onChange={(event) =>
                                  this.handleChangeFrequencyCap(
                                    event,
                                    'TimeToLive',
                                    frequencyCapIdx,
                                  )
                                }
                              />
                            </Grid>

                            <Grid item style={{ width: 75, margin: '10px 0 0 10px' }}>
                              <Select
                                style={{ width: 75 }}
                                value={
                                  frequencyCap.TimeToLive % 86400 === 0
                                    ? FrequencyCapUnitDays
                                    : FrequencyCapUnitHours
                                }
                                disabled={
                                  frequencyCap.Active === AdserverOrderFrequencyCapActiveInactive
                                }
                                onChange={(event: React.ChangeEvent<{ value: any }>) =>
                                  this.handleChangeFrequencyCap(event, 'Unit', frequencyCapIdx)
                                }
                              >
                                <MenuItem
                                  key={FrequencyCapUnitHours}
                                  value={FrequencyCapUnitHours}
                                  style={{ width: '100%' }}
                                >
                                  Hours
                                </MenuItem>
                                <MenuItem
                                  key={FrequencyCapUnitDays}
                                  value={FrequencyCapUnitDays}
                                  style={{ width: '100%' }}
                                >
                                  Days
                                </MenuItem>
                              </Select>
                            </Grid>

                            {order.AdserverOrderFrequencyCaps.length > 1 && (
                              <Tooltip title='Delete'>
                                <IconButton
                                  style={{ margin: '5px 0' }}
                                  aria-label='delete'
                                  onClick={() => this.handleDeleteFrequencyCap(frequencyCapIdx)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </>
                        )}
                      </Grid>
                    );
                  },
                )}
              </Grid>
              <Button
                style={{ color: '#5865E8', paddingLeft: 15, paddingRight: 15, marginTop: 10 }}
                onClick={this.handleAddNewFrequencyCap}
              >
                + Add Frequency Cap
              </Button>
            </Grid>

            <Divider style={{ margin: 10, height: 1, color: 'black' }} />

            {/* Dayparting */}
            <Grid
              container
              style={{
                width: '100%',
                maxWidth: 800,
                marginTop: 5,
                marginLeft: 25,
              }}
            >
              <Grid container direction='row'>
                <Typography className={classes.titleTypography}>Dayparting</Typography>
                <Tooltip
                  title='When Order Dayparting is applied, it will apply to every individual Line Item under this Order.'
                  placement='bottom-start'
                >
                  <InfoIcon
                    style={{
                      verticalAlign: 'bottom',
                      width: '1rem',
                      color: '#929599',
                      marginTop: 1,
                      marginLeft: 10,
                    }}
                  />
                </Tooltip>
              </Grid>
              <Grid item>
                <span
                  className={classes.spanActive}
                  style={{ padding: '20px 0 15px 20px', margin: '5px 0' }}
                >
                  {isDaypartingActive ? 'Active' : 'Inactive'}
                </span>
                <Switch
                  className={classes.centeredSwitch}
                  checked={isDaypartingActive}
                  name={'DaypartingActiveSwitch'}
                  onChange={(event) => {
                    handleChangeDayparting(
                      event,
                      Object.assign({}, order, {
                        DaypartingIsAllowed: event.target.checked
                          ? EAllowStatus.ALLOW
                          : EAllowStatus.DENY,
                      }),
                    );
                  }}
                  value={isDaypartingActive}
                  color='secondary'
                />
              </Grid>
              <Grid item>
                {isDaypartingActive && (
                  <Grid container style={{ backgroundColor: '#F7FAFF' }}>
                    <Grid item>
                      <span
                        className={classes.spanActive}
                        style={{ padding: '20px 0 15px 20px', margin: '5px 0' }}
                      >
                        {order.DaypartingIsAllowed === EAllowStatus.ALLOW ? 'Include' : 'Exclude'}
                      </span>
                      <ConfirmationDialogButton
                        acceptButtonTitle='Confirm'
                        acceptButtonCallBack={() => {
                          const event = {
                            target: {
                              name: 'DaypartingIsAllowed',
                            },
                          };
                          handleChangeDayparting(
                            event as any,
                            Object.assign({}, order, {
                              DaypartingIsAllowed:
                                order.DaypartingIsAllowed === EAllowStatus.ALLOW
                                  ? EAllowStatus.DENY
                                  : (EAllowStatus.ALLOW as any),
                            }),
                          );
                        }}
                        description={makeTargetingChangeDescription(
                          order.DaypartingIsAllowed === EAllowStatus.ALLOW,
                        )}
                        disabled={!writeAuthorization.isAuthorized}
                        refuseButtonTitle='Cancel'
                        title='Dayparting Include/Exclude'
                        triggerComponentInfo={makeTargetingChangeInfoHover(
                          order.DaypartingIsAllowed === EAllowStatus.ALLOW,
                        )}
                      >
                        <IconButton>
                          {order.DaypartingIsAllowed === EAllowStatus.ALLOW ? (
                            <CheckIcon style={{ color: publicaColors.green }} />
                          ) : (
                            <BlockIcon style={{ color: publicaColors.red }} />
                          )}
                        </IconButton>
                      </ConfirmationDialogButton>
                    </Grid>
                    <Grid item>
                      <DaypartingSelector
                        DaypartingIntervals={daypartingIntervals}
                        classes={classes}
                        handleAddDaypartingPeriod={handleCreateDaypartingPeriod}
                        handleDateChange={handleDaypartingDateChange}
                        handleDayClick={handleDaypartingDayClick}
                        handleDeleteDaypartingPeriod={handleDeleteDaypartingPeriod}
                      />
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      );
    };

    renderLineItems = () => {
      const { order, getOrder } = this.props;
      return (
        <Grid style={{ marginLeft: 20, marginTop: 20 }}>
          <CampaignSettingsListView
            campaigns={order.LineItems}
            orderID={order.ID}
            orderActive={order.Active}
            getOrder={getOrder}
            hideTopbar={true}
          />
        </Grid>
      );
    };

    // define tabs and corresponding render functions
    renderTabs = (): ReactElement => {
      const { location, order } = this.props;
      const tabs: ITab[] = [
        {
          label: 'Line Items',
          render: this.renderLineItems,
        },
        {
          label: 'Order Details',
          render: this.renderDetails,
        },
      ];
      // when we are creating a new order, show the order details first
      // otherwise, we can default to showing the line items first
      if (location.pathname.indexOf('orders/new') > -1) {
        tabs.reverse();
      }
      const activeLineItemIDs = order.LineItems.filter(
        (c: IAdserverCampaign) => c.Active === CampaignActiveActive,
      ).map((c: IAdserverCampaign) => c.ID);
      const showGraph = isExistingOrder(order) && activeLineItemIDs.length > 0;
      return <SettingsTabs marginTop={showGraph ? 0 : 8} tabs={tabs} />;
    };

    render() {
      const {
        snackbarMessage,
        snackbarError,
        AddEditCompanyModelActive,
        companyUpdateCounter,
        lastPressedAddEditCompanyModelType,
      } = this.state;
      const { order, classes, user, session, location } = this.props;
      const { pathname } = location;
      const activeLineItemIDs = order.LineItems.filter(
        (c: IAdserverCampaign) => c.Active === CampaignActiveActive,
      ).map((c: IAdserverCampaign) => c.ID);
      // only show the graph if the order already exists and has active line items
      const showGraph = isExistingOrder(order) && activeLineItemIDs.length > 0;
      return (
        <Panels maxWidth vertical relative minWidthMain>
          <Panel largeHorizontalPadding verticalPadding>
            <ModelTopBar
              title={routeName(pathname)}
              session={session}
              user={user}
              publisher={session.publisher}
              classes={classes}
              delegateElevate={true}
              currentPath={pathname}
              disableTitlePaddingLeft
            />
          </Panel>
          <Panel>{this.renderTopBar()}</Panel>
          <Panel scroll>
            {/* only show live data if the order is not a clone or new and has line items */}
            {showGraph && (
              <Grid style={{ marginTop: spacing.medium }}>
                <LiveData
                  resourceIDs={activeLineItemIDs.join(',')}
                  resourceType={'Campaign'}
                  timezone={session.publisher.Timezone}
                  publisher={this.props.session.publisher}
                  maxWidth='1200px'
                  marginTop={200}
                />
              </Grid>
            )}
            <Panel marginBottom={spacing.large}>{this.renderTabs()}</Panel>
            <AddEditCompanyModel
              publisherID={session.publisher.ID}
              active={AddEditCompanyModelActive}
              forcedSelection={lastPressedAddEditCompanyModelType}
              onClose={() => this.setAddEditCompanyModelActive(false)}
              onUpdate={this.addOrUpdateCompany}
              onDelete={this.deleteCompany}
              counter={companyUpdateCounter}
              username={this.props.user.user.Username}
            />
            <SnackbarView
              open={!!snackbarMessage}
              error={!!snackbarError}
              onClose={this.handleSnackbarClose}
            >
              {snackbarMessage}
            </SnackbarView>
          </Panel>
        </Panels>
      );
    }
  },
);
